// Full-screen loading overlay shown during the initial auth + map data
// load. A premium wireframe globe with orbiting satellite, shimmer sweep,
// and breathing core — all compositor-driven for 60fps smoothness.
export default function LoadingScreen({ fadingOut = false, error = false }) {
  return (
    <div
      className="fixed inset-0 z-[9999] flex flex-col items-center justify-center bg-background"
      style={{
        opacity: fadingOut ? 0 : 1,
        transition: 'opacity 500ms cubic-bezier(0.32, 0.72, 0, 1)',
        pointerEvents: fadingOut ? 'none' : 'auto',
      }}
    >
      <div className="globe-loader" role="status" aria-label="Loading">
        {/* Shimmer sweep — rotating gradient highlight across the globe */}
        <div className="globe-shimmer" />

        {/* Static layer: outer ring, equator, tropics, center dot */}
        <svg viewBox="0 0 100 100" className="globe-layer" xmlns="http://www.w3.org/2000/svg">
          {/* Outer ring with subtle rotation */}
          <g className="globe-ring-outer">
            <circle cx="50" cy="50" r="47" fill="none" stroke="hsl(var(--foreground))" strokeWidth="0.8" strokeOpacity="0.2" />
          </g>
          {/* Dashed accent ring — counter-rotating */}
          <circle
            cx="50" cy="50" r="42"
            fill="none"
            stroke="hsl(var(--foreground))"
            strokeWidth="0.6"
            strokeOpacity="0.15"
            strokeDasharray="2 4"
            className="globe-ring-accent"
          />
          {/* Equator + tropics */}
          <g stroke="hsl(var(--foreground))" strokeWidth="0.8" fill="none" strokeOpacity="0.25">
            <line x1="3" y1="50" x2="97" y2="50" />
            <ellipse cx="50" cy="50" rx="47" ry="7" />
            <ellipse cx="50" cy="50" rx="47" ry="24" strokeOpacity="0.12" />
          </g>
          {/* Breathing core */}
          <circle cx="50" cy="50" r="2.5" fill="hsl(var(--foreground))" className="globe-pulse" />
        </svg>

        {/* Rotating layer: meridians — 2D scaleX oscillation (cannot stutter) */}
        <div className="globe-layer globe-spin">
          <svg viewBox="0 0 100 100" className="globe-svg" xmlns="http://www.w3.org/2000/svg">
            <g stroke="hsl(var(--foreground))" strokeWidth="1" fill="none" strokeOpacity="0.5">
              <ellipse cx="50" cy="50" rx="47" ry="16" />
              <ellipse cx="50" cy="50" rx="32" ry="47" />
              <ellipse cx="50" cy="50" rx="16" ry="47" />
              <ellipse cx="50" cy="50" rx="47" ry="32" strokeOpacity="0.3" />
            </g>
          </svg>
        </div>

        {/* Orbiting satellite dot */}
        <div className="globe-orbit">
          <div className="globe-orbit-dot" />
        </div>
      </div>

      {!error ? (
        <>
          <div className="loader-wordmark mt-7 text-xs font-semibold uppercase tracking-[0.3em] text-foreground/50">
            World Traveler
          </div>
          <div className="loader-dots">
            <span />
            <span />
            <span />
          </div>
        </>
      ) : (
        <div className="mt-8 text-center">
          <div className="text-sm font-medium text-destructive mb-2">Unable to load map data</div>
          <div className="text-xs text-muted-foreground mb-4 max-w-xs">
            The app couldn't load required map resources. Please check your internet connection.
          </div>
        </div>
      )}
    </div>
  );
}